import { useState, useEffect, useCallback } from 'react';

const Catalog = ({ onVideoSelect, userType, onEditVideo }) => {
  const [loadedImages, setLoadedImages] = useState({});
  const [groupedVideos, setGroupedVideos] = useState({});
  const [groupedVideosRandomOrder, setGroupedVideosRandomOrder] = useState([]);
  const [recommendedVideos, setRecommendedVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para controlar eliminación
  const [deletingVideoId, setDeletingVideoId] = useState(null);
  
  const [paramsVersion, setParamsVersion] = useState(0);
  
  const getCurrentParams = () => {
    return {
      filterMode: localStorage.getItem('filterMode') || 'v',
      languageMode: localStorage.getItem('languageMode') || 't',
      debugMode: localStorage.getItem('debugMode') === 'true',
      selectedCollections: JSON.parse(localStorage.getItem('selectedCollections') || '[]'),
      tagsInclude: localStorage.getItem('tagsInclude') || '',
      tagsExclude: localStorage.getItem('tagsExclude') || ''
    };
  };

  const getApiLanguage = useCallback((langMode) => {
    switch (langMode) {
      case 'es': return 'ES';
      case 'en': return 'EN';
      case 't': return 'all';
      default: return 'all';
    }
  }, []);

  const getApiMusicParam = useCallback((filtMode) => {
    switch (filtMode) {
      case 'm': return 'true';
      case 'r': return 'false';
      case 'v': return null;
      default: return null;
    }
  }, []);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const fetchVideos = useCallback(async (filterMode, languageMode, selectedCollections, tagsInclude, tagsExclude) => {
    try {
      setLoading(true);
      setError(null);

      const language = getApiLanguage(languageMode);
      const musicParam = getApiMusicParam(filterMode);
      
      let apiUrl = `https://videokids.visssible.com/backend/get-videos.php?language=${language}`;
      
      if (musicParam !== null) {
        apiUrl += `&music=${musicParam}`;
      }

      // Añadir filtros de colecciones
      if (selectedCollections && selectedCollections.length > 0) {
        apiUrl += `&collections=${encodeURIComponent(selectedCollections.join(','))}`;
      }

      // Añadir filtros de tags
      if (tagsInclude && tagsInclude.trim()) {
        apiUrl += `&tags_include=${encodeURIComponent(tagsInclude.trim())}`;
      }
      if (tagsExclude && tagsExclude.trim()) {
        apiUrl += `&tags_exclude=${encodeURIComponent(tagsExclude.trim())}`;
      }

      console.log('🔍 FETCHING VIDEOS con parámetros:');
      console.log('   filterMode:', filterMode);
      console.log('   languageMode:', languageMode);
      console.log('   selectedCollections:', selectedCollections);
      console.log('   tagsInclude:', tagsInclude);
      console.log('   tagsExclude:', tagsExclude);
      console.log('   URL:', apiUrl);

      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log(`✅ Videos recibidos: ${data.videos.length}`);
        setAllVideos(data.videos);
        
        const filteredVideos = data.videos;
        
        const grouped = filteredVideos.reduce((acc, video) => {
          if (!acc[video.collection]) acc[video.collection] = [];
          acc[video.collection].push(video);
          return acc;
        }, {});
        
        setGroupedVideos(grouped);
        
        const groupedArray = Object.entries(grouped);
        const shuffledGroups = shuffleArray(groupedArray);
        setGroupedVideosRandomOrder(shuffledGroups);
        
        const shuffled = [...filteredVideos].sort(() => 0.5 - Math.random());
        setRecommendedVideos(shuffled.slice(0, 6));
        
        console.log(`✅ Procesamiento completo. Colecciones: ${shuffledGroups.length}`);
      } else {
        throw new Error(data.error || 'Error en la respuesta de la API');
      }
    } catch (err) {
      console.error('❌ Error fetching videos:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getApiLanguage, getApiMusicParam]);

  useEffect(() => {
    const params = getCurrentParams();
    console.log('🔄 useEffect ejecutado con params:', params);
    fetchVideos(
      params.filterMode, 
      params.languageMode,
      params.selectedCollections,
      params.tagsInclude,
      params.tagsExclude
    );
  }, [paramsVersion, fetchVideos]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'filterMode' || e.key === 'languageMode' || e.key === 'debugMode' || 
          e.key === 'selectedCollections' || e.key === 'tagsInclude' || e.key === 'tagsExclude') {
        console.log('📝 Storage cambiado:', e.key, '=', e.newValue);
        setParamsVersion(prev => prev + 1);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    const handleCustomStorageChange = () => {
      console.log('🎯 Evento custom de storage recibido');
      setParamsVersion(prev => prev + 1);
    };
    
    window.addEventListener('localStorageChange', handleCustomStorageChange);
    
    const interval = setInterval(() => {
      const params = getCurrentParams();
      const savedFilterMode = localStorage.getItem('filterMode') || 'v';
      const savedLanguageMode = localStorage.getItem('languageMode') || 't';
      
      if (params.filterMode !== savedFilterMode || params.languageMode !== savedLanguageMode) {
        console.log('⏰ Check periódico: parámetros cambiaron, forzando re-render');
        setParamsVersion(prev => prev + 1);
      }
    }, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const params = getCurrentParams();
    
    allVideos.forEach((video) => {
      const imageToLoad = params.filterMode === 'm' && video.album ? video.album : video.cover;
      const img = new Image();
      img.src = `/covers/${imageToLoad}.png`;
      img.onload = () => setLoadedImages(prev => ({ ...prev, [video.id]: true }));
      img.onerror = () => {
        console.error(`Error loading: /covers/${imageToLoad}.png`);
        setLoadedImages(prev => ({ ...prev, [video.id]: false }));
      };
    });
  }, [allVideos]);

  const currentParams = getCurrentParams();

  const getImageToShow = (video) => {
    if (currentParams.filterMode === 'm' && video.album) {
      return `/covers/${video.album}.png`;
    }
    return `/covers/${video.cover}.png`;
  };

  const getImageAlt = (video) => {
    if (currentParams.filterMode === 'm' && video.album) {
      return `${video.album} (álbum)`;
    }
    return video.cover;
  };

  const getImageContainerClass = () => {
    return currentParams.filterMode === 'm' 
      ? "relative aspect-square overflow-hidden rounded-lg"
      : "relative aspect-video overflow-hidden rounded-lg";
  };

  const getGridClass = () => {
    return currentParams.filterMode === 'm' 
      ? "grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-4"
      : "grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4";
  };

  const getImageFilterClass = () => {
    return currentParams.filterMode === 'r' ? "grayscale" : "";
  };

  const formatDebugField = (label, value) => {
    if (value === null || value === undefined || value === '') {
      return `${label}: <span class="text-gray-400">null</span>`;
    }
    return `${label}: <span class="text-yellow-300">${value}</span>`;
  };

  const handleEditClick = (video, e) => {
    e.stopPropagation();
    console.log(`✏️ Editando video ID: ${video.id}`);
    if (onEditVideo) {
      onEditVideo(video);
    }
  };

  const handleDeleteClick = async (video, e) => {
    e.stopPropagation();
    
    const confirmDelete = window.confirm(`¿Estás seguro de que quieres eliminar el video "${video.cover}"? Esta acción no se puede deshacer.`);
    
    if (!confirmDelete) return;
    
    setDeletingVideoId(video.id);
    console.log(`🗑️ Eliminando video ID: ${video.id}`);
    
    try {
      const response = await fetch('https://videokids.visssible.com/backend/delete-video.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: video.id })
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Video eliminado exitosamente');
        window.dispatchEvent(new CustomEvent('localStorageChange'));
      } else {
        console.error('❌ Error al eliminar:', result.error);
        alert(`Error al eliminar: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      alert(`Error de conexión: ${error.message}`);
    } finally {
      setDeletingVideoId(null);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-6 overflow-y-auto h-[calc(100vh-40px)] flex items-center justify-center">
        <div className="text-white text-xl">
          <i className="fa fa-spinner fa-spin mr-2"></i>
          Cargando videos...
          <div className="text-sm text-gray-400 mt-2">
            Filtro: {currentParams.filterMode === 'v' ? 'Videos' : currentParams.filterMode === 'm' ? 'Música' : 'Radio'} | 
            Idioma: {currentParams.languageMode === 'es' ? 'Español' : currentParams.languageMode === 'en' ? 'Inglés' : 'Todos'}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6 overflow-y-auto h-[calc(100vh-40px)] flex items-center justify-center">
        <div className="text-red-500 text-xl">
          <i className="fa fa-exclamation-triangle mr-2"></i>
          Error: {error}
          <button 
            onClick={() => {
              const params = getCurrentParams();
              fetchVideos(params.filterMode, params.languageMode, params.selectedCollections, params.tagsInclude, params.tagsExclude);
            }}
            className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (allVideos.length === 0) {
    return (
      <div className="w-full p-6 overflow-y-auto h-[calc(100vh-40px)] flex items-center justify-center">
        <div className="text-white text-xl">
          No hay videos disponibles con los filtros seleccionados.
          <div className="text-sm text-gray-400 mt-2">
            Filtro: {currentParams.filterMode} | Idioma: {currentParams.languageMode}
            {currentParams.selectedCollections.length > 0 && ` | Colecciones: ${currentParams.selectedCollections.length} seleccionadas`}
            {(currentParams.tagsInclude || currentParams.tagsExclude) && ` | Tags: ${currentParams.tagsInclude ? 'incluir ' + currentParams.tagsInclude : ''}${currentParams.tagsExclude ? 'excluir ' + currentParams.tagsExclude : ''}`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6 overflow-y-auto h-[calc(100vh-40px)]">
      {currentParams.debugMode && (
        <div className="mb-6 p-4 bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded-lg">
          <h2 className="text-yellow-400 text-lg font-bold mb-2">
            <i className="fa fa-bug mr-2"></i> Modo Debug Activo
          </h2>
          <p className="text-yellow-300 text-sm">
            Mostrando detalles técnicos. Total videos: {allVideos.length}
          </p>
          <p className="text-yellow-200 text-xs mt-2">
            Filtros actuales: {currentParams.filterMode} | Idioma: {currentParams.languageMode}
            {currentParams.selectedCollections.length > 0 && ` | Colecciones: ${currentParams.selectedCollections.join(', ')}`}
            {currentParams.tagsInclude && ` | Tags Include: ${currentParams.tagsInclude}`}
            {currentParams.tagsExclude && ` | Tags Exclude: ${currentParams.tagsExclude}`}
          </p>
          <button 
            onClick={() => setParamsVersion(prev => prev + 1)}
            className="mt-2 px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
          >
            Forzar recarga
          </button>
        </div>
      )}

      {userType !== 'e' && recommendedVideos.length > 0 && (
        <div className="border-white-200 border-b py-6">
          <h2 className="text-white text-2xl font-bold mb-4">
            <i className='fa fa-star'></i> Recomendados
          </h2>
          <div className={getGridClass()}>
            {recommendedVideos.map((video, index) => (
              <div key={`recommended-${video.id || index}`} className="relative">
                <div 
                  onClick={() => onVideoSelect(video.url, video.cover)}
                  className={`${getImageContainerClass()} cursor-pointer`}
                >
                  <img
                    className={`w-full h-full object-cover ${getImageFilterClass()} ${loadedImages[video.id] === false ? 'hidden' : ''}`}
                    src={getImageToShow(video)}
                    alt={getImageAlt(video)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      setLoadedImages(prev => ({ ...prev, [video.id]: false }));
                    }}
                  />
                  {loadedImages[video.id] === false && (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Imagen no disponible</span>
                    </div>
                  )}
                </div>
                
                {currentParams.debugMode && (
                  <div className="mt-2 p-2 bg-gray-900 bg-opacity-80 rounded text-xs">
                    <div className="text-white font-bold mb-1 truncate">ID: {video.id}</div>
                    <div className="text-gray-300 space-y-1">
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('cover', video.cover) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('collection', video.collection) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('language', video.language) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('description', video.description) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('tags', video.tags) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('music', video.music ? 'true' : 'false') }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('album', video.album) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('ethan', video.ethan) }} />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={(e) => handleEditClick(video, e)}
                        className="flex-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded flex items-center justify-center gap-1"
                      >
                        <i className="fa fa-edit text-xs"></i>
                        Editar
                      </button>
                      <button
                        onClick={(e) => handleDeleteClick(video, e)}
                        disabled={deletingVideoId === video.id}
                        className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deletingVideoId === video.id ? (
                          <i className="fa fa-spinner fa-spin text-xs"></i>
                        ) : (
                          <i className="fa fa-trash text-xs"></i>
                        )}
                        Eliminar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {groupedVideosRandomOrder.map(([collection, collectionVideos]) => (
        <div key={collection} className="border-white-200 border-b py-6">
          <h2 className="text-white text-2xl font-bold mb-4 capitalize">
            {collection.replace(/-/g, ' ')}
          </h2>
          <div className={getGridClass()}>
            {collectionVideos.map((video, index) => (
              <div key={`${collection}-${video.id || index}`} className="relative">
                <div 
                  onClick={() => onVideoSelect(video.url, video.cover)}
                  className={`${getImageContainerClass()} cursor-pointer`}
                >
                  <img
                    className={`w-full h-full object-cover ${getImageFilterClass()} ${loadedImages[video.id] === false ? 'hidden' : ''}`}
                    src={getImageToShow(video)}
                    alt={getImageAlt(video)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      setLoadedImages(prev => ({ ...prev, [video.id]: false }));
                    }}
                  />
                  {loadedImages[video.id] === false && (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Imagen no disponible</span>
                    </div>
                  )}
                </div>
                
                {currentParams.debugMode && (
                  <div className="mt-2 p-2 bg-gray-900 bg-opacity-80 rounded text-xs">
                    <div className="text-white font-bold mb-1 truncate">ID: {video.id}</div>
                    <div className="text-gray-300 space-y-1">
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('cover', video.cover) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('collection', video.collection) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('language', video.language) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('description', video.description) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('tags', video.tags) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('music', video.music ? 'true' : 'false') }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('album', video.album) }} />
                      <div dangerouslySetInnerHTML={{ __html: formatDebugField('ethan', video.ethan) }} />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={(e) => handleEditClick(video, e)}
                        className="flex-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded flex items-center justify-center gap-1"
                      >
                        <i className="fa fa-edit text-xs"></i>
                        Editar
                      </button>
                      <button
                        onClick={(e) => handleDeleteClick(video, e)}
                        disabled={deletingVideoId === video.id}
                        className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deletingVideoId === video.id ? (
                          <i className="fa fa-spinner fa-spin text-xs"></i>
                        ) : (
                          <i className="fa fa-trash text-xs"></i>
                        )}
                        Eliminar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Catalog;